import { useEffect, useRef, useState } from 'react';

// Loads Zoom's classic Web SDK (ZoomMtg) from their own CDN, exactly as in
// Zoom's official CDN sample (github.com/zoom/meetingsdk-web-sample/CDN).
// We use this instead of the npm package @zoom/meetingsdk because that
// package's Component View bundle has a broken internal dependency
// (@zoom/download-manager, not published to npm) that breaks Create React
// App's build. This CDN bundle doesn't have that problem and is Zoom's own
// verified-working reference implementation. The SDK takes over rendering a
// full-screen meeting UI on top of the current page — no redirect, no new
// tab, the address bar never leaves our domain.
const ZOOM_SDK_VERSION = '6.1.0';
const ZOOM_CDN = `https://source.zoom.us/${ZOOM_SDK_VERSION}`;

const SCRIPT_URLS = [
  `${ZOOM_CDN}/lib/vendor/react.min.js`,
  `${ZOOM_CDN}/lib/vendor/react-dom.min.js`,
  `${ZOOM_CDN}/lib/vendor/react-redux.min.js`,
  `${ZOOM_CDN}/lib/vendor/redux.min.js`,
  `${ZOOM_CDN}/lib/vendor/redux-thunk.min.js`,
  `${ZOOM_CDN}/zoom-meeting-${ZOOM_SDK_VERSION}.min.js`,
];

let loadingPromise = null;

const loadScript = (src) => new Promise((resolve, reject) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve();
  const script = document.createElement('script');
  script.src = src;
  script.onload = resolve;
  script.onerror = () => reject(new Error(`Failed to load ${src}`));
  document.body.appendChild(script);
});

// Loaded strictly in sequence — react-redux/redux-thunk etc. depend on the
// scripts before them already having set up their globals.
const loadZoomSdk = () => {
  if (window.ZoomMtg) return Promise.resolve();
  if (!loadingPromise) {
    loadingPromise = SCRIPT_URLS.reduce((chain, src) => chain.then(() => loadScript(src)), Promise.resolve());
  }
  return loadingPromise;
};

const ZoomMeetingEmbed = ({ meetingNumber, signature, password, userName, onClose }) => {
  const [error, setError] = useState('');
  const startedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    if (startedRef.current) return;
    startedRef.current = true;

    loadZoomSdk()
      .then(() => {
        if (cancelled) return;
        const { ZoomMtg } = window;
        ZoomMtg.setZoomJSLib(`${ZOOM_CDN}/lib`, '/av');
        ZoomMtg.preLoadWasm();
        ZoomMtg.prepareWebSDK();

        ZoomMtg.init({
          leaveUrl: window.location.href,
          disableCORP: !window.crossOriginIsolated,
          success: () => {
            ZoomMtg.join({
              meetingNumber,
              userName: userName || 'Guest',
              signature,
              userEmail: '',
              passWord: password || '',
              success: () => {},
              error: (res) => {
                if (!cancelled) setError(res?.errorMessage || 'Failed to join the meeting.');
              },
            });
          },
          error: (res) => {
            if (!cancelled) setError(res?.errorMessage || 'Failed to initialize the meeting.');
          },
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load the meeting library. Please check your connection.');
      });

    return () => {
      cancelled = true;
      try { window.ZoomMtg?.leaveMeeting?.({}); } catch { /* no-op */ }
    };
  }, [meetingNumber, signature, password, userName]);

  if (!error) return null;

  return (
    <div className="fixed inset-0 bg-black z-[200] flex items-center justify-center">
      <div className="text-center p-6 max-w-sm">
        <p className="text-red-400 text-sm mb-4">{error}</p>
        <button onClick={onClose} className="px-4 py-2 bg-white/10 text-white rounded-xl text-sm">Close</button>
      </div>
    </div>
  );
};

export default ZoomMeetingEmbed;
